import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ShieldCheck, Building2, UserRound } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { AuthLayout } from "@/pages/Login";
import { OnboardingExit } from "@/components/OnboardingExit";

const RegistryPanel = ({ registry }) => (
  <div className="space-y-2 rounded-xl border border-emerald-200 bg-emerald-50/70 p-4 text-sm" data-testid="npi-registry-panel">
    <p className="flex items-center gap-2 font-semibold text-emerald-800">
      <ShieldCheck className="h-4 w-4" /> Verified in the NPPES NPI Registry
    </p>
    <p className="font-medium text-slate-800">{registry.organization_name}</p>
    {registry.dba_name && registry.legal_name !== registry.dba_name && (
      <p className="text-xs text-slate-500">Legal name: {registry.legal_name}</p>
    )}
    {registry.address && <p className="text-slate-600">{registry.address}</p>}
    <p className="text-slate-600">
      {[registry.city, registry.state, registry.postal_code].filter(Boolean).join(", ")}
    </p>
    {registry.taxonomies?.length > 0 && (
      <p className="text-xs text-slate-500">{registry.taxonomies.join(" · ")}</p>
    )}
  </div>
);

const ROLE_CARDS = [
  { key: "Pharmacist", label: "Pharmacist", hint: "NPI verification required", icon: UserRound },
  { key: "Pharmacy Technician", label: "Pharmacy Technician", hint: "No NPI needed", icon: Building2 },
  { key: "Pharmacy Clerk", label: "Pharmacy Clerk", hint: "No NPI needed", icon: Building2 },
];

export default function NpiOnboarding({ mode }) {
  const isPharmacy = mode === "pharmacy";
  const { refresh } = useAuth();
  const navigate = useNavigate();
  const [role, setRole] = useState(isPharmacy ? "Pharmacy" : "");
  const [npi, setNpi] = useState("");
  const [registry, setRegistry] = useState(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const verify = async () => {
    setBusy(true);
    setError("");
    try {
      const path = isPharmacy ? "/pharmacy/npi/onboard" : "/worker/npi/onboard";
      const { data } = await api.post(path, { npi });
      setRegistry(data.registry);
      await refresh();
    } catch (e) {
      setError(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  const needsNpi = isPharmacy || role === "Pharmacist";

  const switchRole = async () => {
    setError("");
    try {
      await api.post("/auth/switch-role", { role: isPharmacy ? "worker" : "pharmacy" });
      await refresh();
      navigate(isPharmacy ? "/worker/npi" : "/pharmacy/npi", { replace: true });
    } catch (e) {
      setError(apiError(e));
    }
  };

  return (
    <AuthLayout
      title={isPharmacy ? "Verify your pharmacy NPI" : "Let's set up your worker profile"}
      subtitle={
        isPharmacy
          ? "We build your pharmacy profile straight from the official NPPES NPI Registry — no typing addresses."
          : "Pick your role. Pharmacists are verified through the official NPPES NPI Registry."
      }
    >
      <div className="space-y-5" data-testid="npi-onboarding">
        <OnboardingExit backTo="/choose-role" backLabel="Back" />
        <div className="rounded-xl border border-slate-200 bg-slate-50 p-3 text-sm" data-testid="account-type-banner">
          <span className="text-slate-600">Account type: </span>
          <strong className="text-slate-800">{isPharmacy ? "Pharmacy (I need staff)" : "Pharmacy worker (I want shifts)"}</strong>
          <button
            type="button"
            onClick={switchRole}
            className="ml-2 font-medium text-brand hover:underline"
            data-testid="switch-account-type"
          >
            {isPharmacy ? "I'm a worker, not a pharmacy" : "I'm a pharmacy, not a worker"}
          </button>
        </div>
        {!isPharmacy && (
          <div className="space-y-2">
            <p className="field-label">Your role</p>
            <div className="grid gap-2">
              {ROLE_CARDS.map((o) => (
                <button
                  key={o.key}
                  type="button"
                  onClick={() => { setRole(o.key); setRegistry(null); setError(""); }}
                  data-testid={`npi-role-${o.key.toLowerCase().replace(/ /g, "-")}`}
                  className={`flex items-center justify-between rounded-xl border p-3.5 text-left transition-all ${
                    role === o.key ? "border-brand bg-brand-soft/50 shadow-sm" : "border-slate-200 hover:border-brand/40"
                  }`}
                >
                  <span className="text-sm font-medium text-slate-800">{o.label}</span>
                  <span className="text-xs text-slate-500">{o.hint}</span>
                </button>
              ))}
            </div>
          </div>
        )}

        {needsNpi && !registry && (
          <div className="space-y-3">
            <div>
              <label className="field-label" htmlFor="npi-input">
                {isPharmacy ? "Pharmacy NPI (type 2 / organisation)" : "Your individual NPI (type 1)"}
              </label>
              <input
                id="npi-input"
                className="field-input"
                inputMode="numeric"
                placeholder="1234567893"
                value={npi}
                onChange={(e) => setNpi(e.target.value)}
                data-testid="npi-onboard-input"
              />
            </div>
            <p className="text-xs text-slate-500">
              An active, matching NPI record is required to continue. We pull your name, address, city,
              state and ZIP code directly from the registry.
            </p>
            <button
              type="button"
              onClick={verify}
              disabled={busy || npi.trim().length < 10}
              className="btn-primary w-full"
              data-testid="npi-onboard-verify"
            >
              {busy ? "Checking the NPI Registry…" : "Verify & continue"}
            </button>
          </div>
        )}

        {registry && (
          <>
            <RegistryPanel registry={registry} />
            <button
              type="button"
              onClick={() => navigate(isPharmacy ? "/pharmacy/onboarding" : "/worker/onboarding")}
              className="btn-primary w-full"
              data-testid="npi-onboard-continue"
            >
              Continue to your profile
            </button>
            <button
              type="button"
              onClick={() => { setRegistry(null); setNpi(""); }}
              className="text-xs text-slate-500 underline"
              data-testid="npi-onboard-reenter"
            >
              Not my record — enter a different NPI
            </button>
          </>
        )}

        {!isPharmacy && role && role !== "Pharmacist" && (
          <button
            type="button"
            onClick={() => navigate(`/worker/onboarding?role=${encodeURIComponent(role)}`)}
            className="btn-primary w-full"
            data-testid="npi-onboard-skip"
          >
            Continue as {role}
          </button>
        )}

        {error && (
          <p className="rounded-lg bg-amber-50 px-3 py-2 text-sm text-amber-900" data-testid="npi-onboard-error">
            {error}
          </p>
        )}
      </div>
    </AuthLayout>
  );
}
